import queryString from 'query-string';
import React from 'react';
import {useLocation, useNavigate} from 'react-router-dom';
import {Filters, SortingRule, TableInstance} from 'react-table';

import * as routeUtils from 'app/utils/routeUtils';

const SORT_URL_KEY = 'sort';

export function isFilterActive(value: unknown): boolean {
  return value !== undefined && value !== null && value !== '';
}

export function countActiveFilters<D extends object>(filters: Filters<D>): number {
  return filters.filter((f) => isFilterActive(f.value)).length;
}

export interface TableUrlState<D extends object> {
  filters: Filters<D>;
  sortBy: SortingRule<D>[];
}

/**
 * Reads and writes a table's filters and single-column sort as query params, so
 * the view is bookmarkable. `filterUrlKeys` maps a column id to its param.
 * `normalizeFilterValue` lets a column drop a value that doesn't parse, so it
 * isn't counted as an active filter.
 */
export function makeTableUrlState<D extends object>({
  filterUrlKeys,
  sortableColumnIds,
  defaultSortBy,
  normalizeFilterValue = (_columnId, value) => value,
}: {
  filterUrlKeys: Record<string, string>;
  sortableColumnIds: string[];
  defaultSortBy: SortingRule<D>[];
  normalizeFilterValue?: (columnId: string, value: string | undefined) => string | undefined;
}) {
  const urlKeys = [...Object.values(filterUrlKeys), SORT_URL_KEY];

  function tableStateFromSearch(search: string): TableUrlState<D> {
    const params = queryString.parse(search);

    const filters: Filters<D> = [];
    Object.entries(filterUrlKeys).forEach(([columnId, key]) => {
      const value = normalizeFilterValue(
        columnId,
        routeUtils.getStringParamFromQueryParams(params, key)
      );
      if (isFilterActive(value)) {
        filters.push({id: columnId, value});
      }
    });

    let sortBy = defaultSortBy;
    const sortParam = routeUtils.getStringParamFromQueryParams(params, SORT_URL_KEY);
    if (sortParam) {
      const desc = sortParam.startsWith('-');
      const id = desc ? sortParam.slice(1) : sortParam;
      if (sortableColumnIds.includes(id)) {
        sortBy = [{id, desc}];
      }
    }

    return {filters, sortBy};
  }

  /** Writes the table state into `search`, leaving unrelated params alone. */
  function searchWithTableState(
    search: string,
    filters: Filters<D>,
    sortBy: SortingRule<D>[]
  ): string {
    const params = queryString.parse(search);
    urlKeys.forEach((key) => delete params[key]);

    filters.forEach(({id, value}) => {
      const key = filterUrlKeys[id];
      if (key && isFilterActive(value)) {
        params[key] = String(value);
      }
    });

    const [sort] = sortBy;
    const [defaultSort] = defaultSortBy;
    if (sort && !(sort.id === defaultSort.id && !!sort.desc === !!defaultSort.desc)) {
      params[SORT_URL_KEY] = `${sort.desc ? '-' : ''}${sort.id}`;
    }

    const next = queryString.stringify(params);
    return next ? `?${next}` : '';
  }

  return {tableStateFromSearch, searchWithTableState};
}

/** Keeps the query string in step with the table's filters and sort. */
export function useSyncTableStateToUrl<D extends object>(
  table: TableInstance<D>,
  searchWithTableState: (search: string, filters: Filters<D>, sortBy: SortingRule<D>[]) => string
) {
  const location = useLocation();
  const navigate = useNavigate();

  React.useEffect(() => {
    const nextSearch = searchWithTableState(
      location.search,
      table.state.filters,
      table.state.sortBy
    );
    if (nextSearch !== location.search) {
      navigate(
        {pathname: location.pathname, search: nextSearch, hash: location.hash},
        {replace: true}
      );
    }
  }, [table.state.filters, table.state.sortBy, location, navigate, searchWithTableState]);
}
